import { NetworkStatus, useQuery } from "@apollo/client";
import { Dispatch, useEffect, type JSX } from "react";
import { Controller, useFormContext } from "react-hook-form";

import FormLoading from "components/form/components/loading";
import FormFieldGitSparseCheckoutPaths from "components/FormFields/GitSparseCheckoutPaths";
import MissingDataBanner from "components/MissingDataBanner";
import Box, { BoxProps } from "ds/components/Box";
import FormField from "ds/components/Form/Field";
import IconEmphasized from "ds/components/IconEmphasized";
import Input from "ds/components/Input";
import Tile from "ds/components/Tile";
import TileContent from "ds/components/Tile/Content";
import TileTitle from "ds/components/Tile/Title";
import TileWrapper from "ds/components/Tile/Wrapper";
import useTypedFlags from "hooks/useTypedFlags";
import { VcsProvider } from "types/generated";
import { checkWithMultipleVCSIntegrations } from "utils/vcs";
import Card from "ds/components/Card";

import VcsBranchesField from "./BranchesField";
import { VCS_PROVIDERS_IN_SPACE } from "./gql";
import VcsIntegrationField from "./IntegrationField";
import ProjectRootTooltip from "./ProjectRootTooltip";
import VcsRepositoryField from "./RepositoryField";
import VcsRepositoryUrlField from "./RepositoryUrlField";
import TileCheckboxGrid from "./TileCheckboxGrid";
import {
  InternalFormData,
  SourceCodeProjects,
  SourceCodeSettingsField,
  VcsProviderInSpacesGql,
} from "./types";
import useErrorHandlerSourceCode from "./useErrorHandlerSourceCode";
import { useVcsProviderConfig } from "./useVcsConfig";
import { getTooltipAnalyticsProps } from "./utils";

export type FormSourceCodeProps = {
  spaceId: string;
  setInternalFormData?: Dispatch<React.SetStateAction<InternalFormData>>;
  setAccountOrAuthErrorContent?: (content: JSX.Element) => void;
  provider?: VcsProvider;
  vcsIntegrationId?: string;
  repository?: string;
  analyticsVersion?: string;
  resetFormFields: (type: VcsProvider) => void;
  projectType: SourceCodeProjects;
  innerFormMargin?: BoxProps["margin"];
  innerFormGap?: BoxProps["gap"];
  useGitCheckout?: boolean;
};

const FormSourceCode = ({
  spaceId,
  setInternalFormData,
  provider,
  vcsIntegrationId,
  repository,
  setAccountOrAuthErrorContent,
  analyticsVersion,
  resetFormFields,
  projectType,
  innerFormMargin = "x-large 0 large 0",
  innerFormGap = "large",
  useGitCheckout,
}: FormSourceCodeProps) => {
  const { enableSparseCheckout } = useTypedFlags();

  const { control, setValue, register } = useFormContext<SourceCodeSettingsField>();

  const { data, loading, error, refetch, networkStatus } = useQuery<VcsProviderInSpacesGql>(
    VCS_PROVIDERS_IN_SPACE,
    {
      variables: {
        spaceId,
      },
      skip: !spaceId,
    }
  );

  useErrorHandlerSourceCode(setAccountOrAuthErrorContent, error);

  const vcsProviderConfig = useVcsProviderConfig(data?.providersInSpace);

  const withMultipleVCSIntegrations = checkWithMultipleVCSIntegrations(provider);

  const waitForVCSIntegrationId = withMultipleVCSIntegrations && !vcsIntegrationId;

  const handleChangeProvider = (fieldValue: VcsProvider | undefined, type: VcsProvider) => () => {
    if (type !== fieldValue) {
      resetFormFields(type);

      setInternalFormData?.((state) => ({
        ...state,
        vcsIntegrationLabel: undefined,
        useGitCheckout: undefined,
      }));
    }
  };

  const shouldShowGitSparseCheckout =
    enableSparseCheckout &&
    (useGitCheckout ||
      provider === VcsProvider.Git ||
      (provider === VcsProvider.Github && data?.githubIntegration?.useGitCheckout));

  useEffect(() => {
    if (!provider) {
      setValue("provider", vcsProviderConfig[0]?.type, { shouldDirty: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vcsProviderConfig]);

  if (loading && networkStatus !== NetworkStatus.refetch) {
    return <FormLoading />;
  }

  return (
    <>
      {!data && (
        <Box direction="column" margin="large 0 0 0">
          <MissingDataBanner
            refreshLoading={loading && networkStatus === NetworkStatus.refetch}
            refreshHandler={refetch}
          />
        </Box>
      )}
      {data && (
        <>
          <Controller
            name="provider"
            control={control}
            render={({ field }) => (
              <Box direction="column" gap="x-large">
                <TileCheckboxGrid>
                  {vcsProviderConfig.map(({ name, logo, type }) =>
                    vcsProviderConfig.length === 1 ? (
                      <TileWrapper key={type}>
                        <TileContent direction="row" align="center">
                          <IconEmphasized icon={logo} />
                          <TileTitle>{name}</TileTitle>
                        </TileContent>
                      </TileWrapper>
                    ) : (
                      <Tile
                        key={type}
                        selected={field.value === type}
                        title={name}
                        icon={logo}
                        onClick={handleChangeProvider(field.value, type)}
                        indicator="radio"
                      />
                    )
                  )}
                </TileCheckboxGrid>
              </Box>
            )}
          />
          <Box direction="column" margin={innerFormMargin} gap={innerFormGap}>
            {withMultipleVCSIntegrations && provider && spaceId && (
              <VcsIntegrationField
                provider={provider}
                spaceId={spaceId}
                setInternalFormData={setInternalFormData}
                analyticsVersion={analyticsVersion}
                projectType={projectType}
              />
            )}

            {provider && provider !== VcsProvider.Git && !waitForVCSIntegrationId && (
              <VcsRepositoryField analyticsVersion={analyticsVersion} projectType={projectType} />
            )}
            {provider === VcsProvider.Git && (
              <VcsRepositoryUrlField
                analyticsVersion={analyticsVersion}
                projectType={projectType}
              />
            )}
            {repository && !waitForVCSIntegrationId && (
              <VcsBranchesField analyticsVersion={analyticsVersion} projectType={projectType} />
            )}

            <FormField
              label="Project root"
              isOptional
              {...getTooltipAnalyticsProps("Source Code", "Project root", projectType, {
                provider: provider,
              })}
              tooltipInfoVariant="modal"
              tooltipInfo={<ProjectRootTooltip projectType={projectType} />}
              noMargin
            >
              {({ ariaInputProps }) => (
                <Input
                  placeholder="Defaults to root of the repo"
                  {...register("projectRoot", {
                    setValueAs: (value: string) => value.trim(),
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>
            {shouldShowGitSparseCheckout && (
              <Card variant="secondary" contentGap="large">
                <FormFieldGitSparseCheckoutPaths />
              </Card>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default FormSourceCode;
